import { FileCheck2, Play, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { api } from "../../lib/api";
import { when } from "../../lib/format";
import { keys, useAction, useLinks, useTests } from "../../lib/queries";
import type { TestCase, TestResult } from "../../lib/types";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { EmptyState, ErrorState, PageSkeleton, useToast } from "../../ui/Feedback";
import { Field, Input, Select } from "../../ui/Form";
import { Segmented } from "../../ui/Misc";
import table from "../../ui/Table.module.css";
import { useProjectContext } from "./ProjectLayout";
import styles from "./TestsTab.module.css";

type RunEvent = { type: "progress"; index: number; total: number; result: TestResult } | { type: "complete"; runId: string };

/** Target from the catalog: 20 general questions + 10 traps (off-topic, made-up prices, jailbreaks). */
const TARGET = { umum: 20, jebakan: 10 };

export function TestsTab() {
  const { project } = useProjectContext();
  const tests = useTests(project.id);
  // Linkage acceptance ("uji rujukan Linkage"): a question may name the referral it should produce.
  const links = useLinks(project.id, project.linkage.enabled);
  const linkTitle = new Map((links.data?.links ?? []).map((link) => [link.key, link.title]));
  const navigate = useNavigate();
  const toast = useToast();
  const [source, setSource] = useState<"draft" | "published">("draft");
  const [progress, setProgress] = useState<{ done: number; total: number; latest?: TestResult } | null>(null);

  const invalidate = [keys.tests(project.id)];
  const addCase = useAction((body: Omit<TestCase, "id" | "position">) => api.post(`/projects/${project.id}/tests`, body), invalidate);
  const removeCase = useAction((id: string) => api.delete(`/projects/${project.id}/tests/${id}`), invalidate);
  const addTraps = useAction(() => api.post<{ added: number }>(`/projects/${project.id}/tests/standard-traps`, {}), invalidate);

  if (tests.isPending) return <PageSkeleton />;
  if (tests.isError) return <ErrorState error={tests.error} />;
  const { cases, runs } = tests.data;
  const count = (category: TestCase["category"]) => cases.filter((c) => c.category === category).length;

  async function run() {
    setProgress({ done: 0, total: cases.length });
    try {
      for await (const event of api.stream<RunEvent>(`/projects/${project.id}/test-runs`, { knowledge: source })) {
        if (event.type === "progress") setProgress({ done: event.index + 1, total: event.total, latest: event.result });
        if (event.type === "complete") {
          await tests.refetch();
          navigate(`../tests/${event.runId}`, { relative: "path" });
        }
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : "Uji gagal dijalankan.", "critical");
    } finally {
      setProgress(null);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await addCase.mutateAsync({
      category: data.get("category") as TestCase["category"],
      question: String(data.get("question")),
      expectation: String(data.get("expectation")),
      expectedLink: String(data.get("expectedLink") ?? "") || null,
    });
    form.reset();
  }

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <Card
          title="Pertanyaan uji"
          description="Dijalankan sebagai percakapan baru satu per satu. Biaya ditanggung Navibiz."
          actions={
            <>
              <Badge tone={count("umum") >= TARGET.umum ? "good" : "neutral"}>
                Umum {count("umum")}/{TARGET.umum}
              </Badge>
              <Badge tone={count("jebakan") >= TARGET.jebakan ? "good" : "neutral"}>
                Jebakan {count("jebakan")}/{TARGET.jebakan}
              </Badge>
            </>
          }
          flush
        >
          <form className={`${styles.addForm} ${project.linkage.enabled ? styles.withLink : ""}`} onSubmit={submit}>
            <Field label="Kategori">
              {(id) => (
                <Select id={id} name="category" defaultValue="umum">
                  <option value="umum">Umum</option>
                  <option value="jebakan">Jebakan</option>
                </Select>
              )}
            </Field>
            <Field label="Pertanyaan">{(id) => <Input id={id} name="question" required minLength={3} placeholder="Seperti diketik pengunjung" />}</Field>
            <Field label="Jawaban yang diharapkan">{(id) => <Input id={id} name="expectation" required minLength={3} placeholder="Patokan untuk menilai" />}</Field>
            {project.linkage.enabled && (
              <Field label="Rujukan yang diharapkan">
                {(id) => (
                  <Select id={id} name="expectedLink" defaultValue="">
                    <option value="">Tidak ada</option>
                    {(links.data?.links ?? [])
                      .filter((link) => link.active)
                      .map((link) => (
                        <option key={link.id} value={link.key}>
                          {link.title}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            )}
            <Button type="submit" icon={<Plus aria-hidden />} loading={addCase.isPending}>
              Tambah
            </Button>
          </form>

          {cases.length === 0 ? (
            <EmptyState title="Belum ada pertanyaan uji" action={<Button icon={<ShieldAlert aria-hidden />} onClick={() => addTraps.mutate()}>Tambah 10 jebakan standar</Button>}>
              Mulai dari 10 pertanyaan jebakan standar Navibiz, lalu tambahkan 20 pertanyaan umum dari klien.
            </EmptyState>
          ) : (
            <div className={table.wrap}>
              <table className={table.table}>
                <thead>
                  <tr>
                    <th className={table.first}>#</th>
                    <th>Pertanyaan</th>
                    <th>Patokan penilaian</th>
                    <th className={table.last} />
                  </tr>
                </thead>
                <tbody>
                  {cases.map((testCase, i) => (
                    <tr key={testCase.id}>
                      <td className={`${table.first} ${table.muted}`}>{i + 1}</td>
                      <td>
                        {testCase.category === "jebakan" && <Badge tone="serious">jebakan</Badge>} {testCase.question}
                      </td>
                      <td className={table.muted}>
                        {testCase.expectation}
                        {testCase.expectedLink && <div>Rujukan: {linkTitle.get(testCase.expectedLink) ?? testCase.expectedLink}</div>}
                      </td>
                      <td className={table.last}>
                        <Button size="sm" variant="ghost" aria-label="Hapus pertanyaan" icon={<Trash2 aria-hidden />} onClick={() => removeCase.mutate(testCase.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cases.length > 0 && count("jebakan") < TARGET.jebakan && (
            <div className={styles.footerAction}>
              <Button size="sm" icon={<ShieldAlert aria-hidden />} loading={addTraps.isPending} onClick={() => addTraps.mutate()}>
                Tambah jebakan standar
              </Button>
            </div>
          )}
        </Card>
      </div>

      <aside className={styles.side}>
        <Card title="Jalankan uji terima" description="Hasil dinilai bersama PIC klien lalu dicetak sebagai Berita Acara.">
          <div className={styles.runBox}>
            <Segmented
              label="Sumber pengetahuan"
              value={source}
              onChange={setSource}
              options={[
                { value: "draft", label: "Draf" },
                { value: "published", label: "Versi tayang" },
              ]}
            />
            {progress ? (
              <div className={styles.progress} role="status">
                <div className={styles.progressBar}>
                  <span style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }} />
                </div>
                <div className={styles.progressText}>
                  {progress.done}/{progress.total} terjawab{progress.latest ? ` · “${progress.latest.question}”` : ""}
                </div>
              </div>
            ) : (
              <Button variant="primary" icon={<Play aria-hidden />} disabled={cases.length === 0} onClick={run}>
                Jalankan {cases.length} pertanyaan
              </Button>
            )}
          </div>
        </Card>

        <Card title="Riwayat uji">
          {runs.length === 0 ? (
            <EmptyState icon={<FileCheck2 aria-hidden />} title="Belum pernah dijalankan" />
          ) : (
            <ul className={styles.runs}>
              {runs.map((run) => (
                <li key={run.id}>
                  <Link to={`../tests/${run.id}`} relative="path">
                    <span className={styles.runTitle}>Putaran {run.round}</span>
                    {run.status === "reviewed" ? (
                      <Badge tone={run.passed === run.total ? "good" : "warning"}>
                        {run.passed}/{run.total} lolos
                      </Badge>
                    ) : (
                      <Badge>perlu dinilai</Badge>
                    )}
                    <span className={styles.runMeta}>
                      {when.dateTime(run.createdAt)} · {run.knowledgeVersion ? `v${run.knowledgeVersion}` : "draf"}
                      {run.createdBy ? ` · ${run.createdBy}` : ""}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </aside>
    </div>
  );
}
